import { useEffect } from "react";

const EMAIL = "info@libraplanet.bg";

const renderWithEmailLinks = (text) => {
  const parts = text.split(EMAIL);
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <a
          href={`mailto:${EMAIL}`}
          data-testid="privacy-email-link"
          className="underline decoration-[var(--line)] underline-offset-2 transition-colors duration-200 hover:text-[var(--ink)]"
        >
          {EMAIL}
        </a>
      )}
    </span>
  ));
};

const SECTIONS = [
  {
    title: "1. About this website",
    body: [
      "Intuitive Shoes is a crowdsourced footwear fit recommendation project designed to help shoppers make more informed size choices and to explore better sizing experiences for ecommerce retailers.",
      "This website provides information about Intuitive Shoes and links to the Intuitive Shoes consumer application and retailer demo.",
    ],
  },
  {
    title: "2. Information we collect",
    body: [
      "IntuitiveShoes.com does not currently provide user accounts, registration, checkout, or an on-site contact form.",
      "We do not intentionally collect personal information directly through forms on this website.",
      "If analytics, essential technical services, or similar technologies are used to operate and understand the performance of the website, they may process limited technical information such as browser type, device type, approximate location derived from IP address, referring pages, and interactions with the website.",
      "We do not use this website to sell personal information.",
    ],
  },
  {
    title: "3. Contact by email",
    body: [
      "The \u201cLet\u2019s Talk\u201d button on this website uses a mailto: link that allows visitors to contact us at info@libraplanet.bg using their own email application.",
      "If you contact us by email, we will receive the information you choose to provide, such as your email address, name, company information, and the contents of your message.",
      "We use this information only as reasonably necessary to respond to your enquiry, discuss a potential business relationship, or continue correspondence you have requested.",
    ],
  },
  {
    title: "4. Links to other services",
    body: [
      "IntuitiveShoes.com contains links to other websites and services, including the Intuitive Shoes consumer application and retailer demonstration website.",
      "These services may have their own privacy practices and policies. When you leave IntuitiveShoes.com, the privacy practices of the destination website or service apply.",
    ],
  },
  {
    title: "5. Cookies and similar technologies",
    body: [
      "IntuitiveShoes.com may use technologies that are necessary for the website to function properly and, where implemented, privacy-conscious analytics technologies to understand general website usage and performance.",
      "Where required by applicable law, non-essential cookies or similar technologies will only be used after appropriate consent has been obtained.",
    ],
  },
  {
    title: "6. Legal basis for processing",
    body: [
      "Where the General Data Protection Regulation (GDPR) or similar data protection legislation applies, personal information may be processed on one or more of the following legal bases:",
    ],
    list: [
      "your consent;",
      "our legitimate interests in operating and improving the website and responding to business enquiries;",
      "taking steps at your request before entering into a contract;",
      "compliance with a legal obligation.",
    ],
  },
  {
    title: "7. Data retention",
    body: [
      "Personal information received through direct email correspondence is retained only for as long as reasonably necessary for the purpose for which it was provided, including responding to enquiries, maintaining relevant business correspondence, and complying with applicable legal obligations.",
    ],
  },
  {
    title: "8. Data sharing",
    body: [
      "We do not sell or rent your personal information.",
      "Information may be processed by service providers where necessary to operate the website, provide hosting or technical infrastructure, maintain security, or deliver services associated with the website. Such processing is limited to what is reasonably necessary for those purposes.",
      "Information may also be disclosed where required by law or necessary to establish, exercise, or defend legal claims.",
    ],
  },
  {
    title: "9. International data transfers",
    body: [
      "Some technology providers may process information outside the European Economic Area. Where applicable, appropriate safeguards required by data protection law will be relied upon for such transfers.",
    ],
  },
  {
    title: "10. Your rights",
    body: [
      "Depending on applicable law and your location, you may have rights concerning your personal information, including the right to:",
    ],
    list: [
      "request access to your personal information;",
      "request correction of inaccurate information;",
      "request deletion of your information;",
      "restrict or object to certain processing;",
      "withdraw consent where processing is based on consent;",
      "request data portability where applicable;",
      "lodge a complaint with the competent data protection authority.",
    ],
    after: "To exercise a privacy-related right concerning this website, contact us at info@libraplanet.bg.",
  },
  {
    title: "11. Security",
    body: [
      "Reasonable technical and organisational measures are used to protect information associated with the website. However, no method of transmission or storage on the internet can be guaranteed to be completely secure.",
    ],
  },
  {
    title: "12. Changes to this Privacy Policy",
    body: [
      "This Privacy Policy may be updated when the website, its services, or applicable legal requirements change. The latest version will always be published on this page with its current \u201cLast updated\u201d date.",
    ],
  },
  {
    title: "13. Contact",
    body: [
      "For questions about this Privacy Policy or the handling of personal information associated with IntuitiveShoes.com, contact:",
      "Email: info@libraplanet.bg",
      "Website: IntuitiveShoes.com",
    ],
  },
];

const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Privacy Policy — Intuitive Shoes";
    return () => {
      document.title = "Intuitive Shoes — Crowdsourced Fit Intelligence for Footwear";
    };
  }, []);

  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <header className="border-b border-[var(--line)]">
        <div className="container-x flex items-center justify-between px-6 py-4">
          <a href="/" data-testid="privacy-brand-link" className="font-display text-xl tracking-tight">
            Intuitive Shoes
          </a>
          <a
            href="/"
            data-testid="privacy-back-link"
            className="text-sm text-[var(--ink-soft)] transition-colors duration-200 hover:text-[var(--ink)]"
          >
            ← Back to homepage
          </a>
        </div>
      </header>

      <main className="container-x px-6 py-16 md:py-24">
        <div className="max-w-2xl">
          <h1 className="font-display text-4xl leading-[1.1] tracking-tight sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm text-[var(--ink-soft)]">Last updated: August 2026</p>
          <p className="mt-8 text-base leading-relaxed text-[var(--ink-soft)]">
            This Privacy Policy explains how information is handled when you visit
            IntuitiveShoes.com, the informational website for Intuitive Shoes.
          </p>

          {SECTIONS.map((section) => (
            <section key={section.title} className="mt-12">
              <h2 className="font-display text-xl tracking-tight md:text-2xl">{section.title}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph} className="mt-4 text-base leading-relaxed text-[var(--ink-soft)]">
                  {renderWithEmailLinks(paragraph)}
                </p>
              ))}
              {section.list && (
                <ul className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-[var(--ink-soft)]">
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
              {section.after && (
                <p className="mt-4 text-base leading-relaxed text-[var(--ink-soft)]">{renderWithEmailLinks(section.after)}</p>
              )}
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-[var(--line)]">
        <div className="container-x flex flex-col gap-2 px-6 py-6 text-xs text-[var(--ink-soft)] md:flex-row md:justify-between">
          <span>Intuitive Shoes</span>
          <span>Fit intelligence built from real-world experience.</span>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
